import React, { useState, useEffect, useRef } from 'react';
import styles from './DebateFormatCarousel.module.css';
import { debateTypes } from '../debateFormatsDataV3';
import { DEBATE_FORMATS_META } from '../debateFormatMeta';
import { HiChevronLeft, HiChevronRight } from 'react-icons/hi2';
import Button from '../../ui/Button';

export default function DebateFormatCarousel({ onSelect }) {
  const [query, setQuery] = useState('');
  const [expanded, setExpanded] = useState(null);
  const [centered, setCentered] = useState(null);
  
  const carouselRef = useRef();
  const scrollIndicator = useRef();
  
  const filtered = debateTypes.filter(f =>
    f.title.toLowerCase().includes(query.toLowerCase())
  );

  const scroll = dir => {
    carouselRef.current?.scrollBy({ left: dir * 300, behavior: 'smooth' });
  };

  useEffect(() => {
    if (!filtered.find(f => f.id === expanded)) {
      setExpanded(null);
    }
  }, [filtered]);


  // Update scroll indicator
  const handleScroll = () => {
    const el = carouselRef.current;
    if (!el) return;
    const ratio = el.scrollLeft / (el.scrollWidth - el.clientWidth);
    scrollIndicator.current.style.transform = `scaleX(${ratio.toFixed(2)})`;
  };
  
  const OnCardSelected = (id) => {
	if(onSelect)onSelect(id);
	setCentered(id);
  };

  // Auto-highlight centered card
  useEffect(() => {
    const el = carouselRef.current;
    if (!el) return;
	return;
    const cards = Array.from(el.children);
    const obs = new IntersectionObserver(
      entries => {
        entries.forEach(e => {
		  if (e.isIntersecting && e.intersectionRatio > 0.6) {
            setCentered(e.target.dataset.id);
          }
        });
      },
      { root: el, threshold: [0.6] }
    );
    cards.forEach(c => obs.observe(c));
    return () => obs.disconnect();
  }, [filtered]);

  useEffect(() => {
    handleScroll();
  }, [filtered]);


  return (
    <div className={styles.wrapper}>
      <input
        type="search"
        className={styles.search}
        placeholder="Search formats..."
        value={query}
        onChange={e => setQuery(e.target.value)}/>

      <div className={styles.carouselContainer}>
        <Button
          className={styles.navBtn}
          onClick={() => scroll(-1)}
          disabled={!filtered.length}>
          <HiChevronLeft />
        </Button>
          <div className={styles.carousel} ref={carouselRef} onScroll={handleScroll}>
            {filtered.map(fmt => {
              const totalTimeSec = (fmt.turns || []).reduce((acc, t) => acc + (t?.durationSec || 0), 0);
              const totalMinutes = Math.floor(totalTimeSec / 60);
              const totalSeconds = totalTimeSec % 60;
              const meta = DEBATE_FORMATS_META.find(m => m.id === fmt.id) || {};
              const Icon = fmt.icon;
              const isExpanded = expanded === fmt.id;
              const isCentered = centered === fmt.id;

              return (
                <div key={fmt.id} data-id={fmt.id} className={`${styles.card} ${isExpanded ? styles.active : ''} ${isCentered ? styles.centered : ''}`} onClick={() => OnCardSelected(fmt.id)}>
                  <div className={styles.iconWrap}>
                  <Icon className={styles.icon} />
                  </div>
                  <h3>{fmt.title}</h3>
                  <p className={styles.description}>{fmt.description}</p>
                  <div className={styles.section}>
                  <strong>Sample:</strong>
                  <span>{fmt.sample}</span>
                  </div>
                  <div className={styles.section}>
                  <strong>Rules:</strong>
                  <ul>
                  {fmt.participants && (
                  <li>
                  Participants: {fmt.participants.min}
                  {fmt.participants.max
                  ? `-${fmt.participants.max}`
                  : '+'}
                  </li>
                  )}
                  {fmt.roles.moderator.exists ? (
                  <li>
                  Moderator:{' '}
                  {fmt.roles.moderator.exists ? 'Required' : 'Optional'}
                  </li>
                  ):(						  
                  <li>
                  Judge:{' '}
                  {fmt.roles.judge.exists ? 'Required' : 'Optional'}
                  </li>
                  )}

                  {fmt.turns && (
                  <li>
                  Turns: {fmt.turns.length}
                  </li>
                  )}
                  <li>
                  Total time: {totalMinutes}m {totalSeconds}s
                  </li>

                  <li>
                  Spectators contribute:{' '}
                  {fmt.spectators.canParticipate ? 'Yes' : 'No'}
                  </li>
                  </ul>
                  </div>
                  <Button className={styles.dropdownBtn} onClick={e => {
                  e.stopPropagation();
                  setExpanded(isExpanded ? null : fmt.id);
                  }}> {isExpanded ? 'Hide Details' : 'Show Details'} </Button>
                  {isExpanded && meta && (
                  <div className={styles.details}>
                  <p>{meta.description}</p>
                  <ul>
                  <li><strong>Structure:</strong> {meta.structure}</li>
                  <li><strong>Best For:</strong> {meta.bestFor}</li>
                  <li><strong>Used In:</strong> {meta.usedIn}</li>
                  </ul>
                  <strong>Features:</strong>
                  <ul>{meta.features.map((ft,i)=><li key={i}>{ft}</li>)}</ul>
                  </div>
                  )}
                </div>
              );
            })}
          </div>
        <Button
          className={styles.navBtn}
          onClick={() => scroll(1)}
          disabled={!filtered.length}>
          <HiChevronRight />
        </Button>
      </div>
      <div className={styles.scrollBar}>
        <div className={styles.scrollIndicator} ref={scrollIndicator} />
      </div>
    </div>
  );
}
